import { useCallback, useEffect, useState } from "react";
import { GoalConfigSchema } from "../../../shared/goals/config";
import { PlanSchema } from "../../../shared/goals/plan";
import { BackLink } from "../../components/ui/BackLink";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { ccApi, type Version, type VersionFull } from "../../lib/ccApi";
import type { GoalView } from "../../lib/goalsApi";
import { DocWalk } from "../goal/DocWalk";
import { PLAN_PARTS, planBodies } from "../goal/FullPlan";
import { useToast } from "../goal/useToast";
import { compareDocs } from "../library/parts";
import { Compare, VersionList, whenOf, type Changes, type Ver } from "../versions/Versions";
import { changes } from "./editDiff";
import { PART_FIELDS, planDoc } from "./planDoc";
import { andList, dayLabel, fmt, parseTime, statePill } from "./text";
import "../../styles/wizard.css";
import "../../styles/cc.css";

/** Who made a version, in the owner's words. */
function by(v: Version): string {
  if (v.made_in === "edit_goal") return "You, in Edit goal";
  if (v.made_by === "owner") return "You";
  if (v.made_by === "agent") return v.made_in === "setup" ? "The agent, approved by you" : "The agent";
  return v.made_by;
}

const verOf = (v: Version): Ver => ({ id: v.id, n: v.version, label: `Version ${v.version}`, mine: v.made_by === "owner", by: by(v), at: v.created_at, note: v.summary });
/** A goal that is approved: only then does an older version come back. */
const APPROVED = ["getting_ready", "live", "paused"];
/** JSON with every object's keys in order, so two versions read alike when only their key order differs. */
const canon = (v: unknown): unknown =>
  Array.isArray(v) ? v.map(canon) : v && typeof v === "object" ? Object.fromEntries(Object.keys(v).toSorted().map((k) => [k, canon((v as Record<string, unknown>)[k])])) : v;
const same = (a: unknown, b: unknown) => JSON.stringify(canon(a ?? null)) === JSON.stringify(canon(b ?? null));

/** The goal's plan (`/goals/:id/plan`): the plan in force, part by part, and every version of it (CC5). */
export function PlanPage({ view, reload }: { view: GoalView; reload: () => Promise<void> }) {
  const g = view.goal;
  const cur = view.current;
  const { toast, say } = useToast();
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [metaHolds, setMetaHolds] = useState(false);
  const [cmp, setCmp] = useState<{ from: Ver; to: Ver } | null>(null);
  const [review, setReview] = useState<Ver | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    ccApi.versions(g.id).then(
      (r) => {
        setVersions(r.versions);
        setMetaHolds(r.metaHolds);
      },
      () => setVersions([]),
    );
  }, [g.id, cur?.version]);
  const load = useCallback(
    async (from: Ver, to: Ver): Promise<Changes> => {
      const [a, b] = await Promise.all([ccApi.version(g.id, from.n), ccApi.version(g.id, to.n)]);
      const doc = (v: VersionFull) => planDoc(GoalConfigSchema.parse(v.config), v.plan ? PlanSchema.parse(v.plan) : null);
      return compareDocs(doc(a.version), doc(b.version));
    },
    [g.id],
  );
  const [show, setShow] = useState(false);
  const [prev, setPrev] = useState<VersionFull | null | undefined>(undefined);
  const before = (cur?.version ?? 0) - 1;
  useEffect(() => {
    if (!show || prev !== undefined || before < 1) return;
    ccApi.version(g.id, before).then((r) => setPrev(r.version), () => setPrev(null));
  }, [show, prev, before, g.id]);

  // Why: a plan the owner never approved never ran; only versions that were in force can come back.
  const shown = (versions ?? []).filter((v) => v.in_force || v.version === cur?.version);
  const latest = shown.find((v) => v.version === cur?.version) ?? null;
  const vers = shown.map(verOf).toReversed();
  const current = latest?.id ?? "";
  const next = Math.max(0, ...(versions ?? []).map((v) => v.version)) + 1;
  const bringBack = async (v: Ver) => {
    if (!cur) return;
    setBusy(true);
    try {
      const r = await ccApi.useVersion(g.id, v.n, cur.version);
      setReview(null);
      await reload();
      say(`Version ${v.n} is back, as version ${r.version}.`);
    } catch (e) {
      say(e instanceof ApiError ? (/Nothing changed/.test(e.message) ? "That is the version in force already." : e.message) : "That did not work. Try again.", false);
    } finally {
      setBusy(false);
    }
  };
  const [, label] = statePill(g.state);
  const plan = cur?.plan ?? null;
  const B = plan ? planBodies(plan) : {};
  const parts = [...(plan ? PLAN_PARTS.map(([id, title]) => ({ id, title })) : []), { id: "versions", title: "Versions" }];
  const old = show && prev?.plan ? prev.plan : null;
  const oldB = old ? planBodies(old) : {};
  const changed = (id: string) => !!(old && plan && (PART_FIELDS[id] ?? []).some((k) => !same(old[k], plan[k])));
  const setup = show && prev && cur ? changes(prev.config, cur.config) : [];
  const moved = PLAN_PARTS.filter(([id]) => changed(id)).map(([, t]) => t);

  return (
    <div className="wrap ga gw ccx cc-gpage">
      <header className="ph flowhead">
        <div className="ctx">
          <BackLink to={`/goals/${encodeURIComponent(g.id)}`} label="Goal" />
          <span className="crumbs fh-crumb">
            <b>Plan</b>
          </span>
        </div>
        <div className="ph-t wz-h">
          <h1 className="ptitle">The plan, version {cur?.version ?? g.version}</h1>
        </div>
        <p className="ph-s goalref">
          <Icon name="target" />
          <span>{g.sentence}</span>
        </p>
      </header>
      <DocWalk
        inline
        parts={parts}
        head={{
          title: cur ? `How we get to ${fmt(cur.config.result.target)}` : "The plan",
          ver: `v${cur?.version ?? g.version}`,
          badge: label,
          about: "The approved plan. The agent changes it only inside your rules, and every change is a new version.",
          small: latest ? `Version ${latest.version} · last change ${dayLabel(parseTime(latest.created_at)).toLowerCase()}` : undefined,
        }}
        marker={(p) => (changed(p.id) ? <span className="chip rl">Changed in v{cur?.version}</span> : null)}
        foot={
          before >= 1 ? (
            <label className="tg">
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
              <span className="sw" />
              Show what changed
            </label>
          ) : undefined
        }
        body={(p) =>
          p.id === "versions" ? (
            <div className="dw-body">
              {show && prev === null && <p className="cc-fine">Version {before} could not be read.</p>}
              {show && prev && (
                <div className="cc-card" style={{ marginBottom: 14 }}>
                  <h4>
                    From version {prev.version} to {cur?.version}
                  </h4>
                  {setup.length ? (
                    <div className="diff">
                      {setup.flatMap((c) => c.lines).map((l, i) => (
                        <div className={`dl ${l.kind}`} key={i}>
                          {l.text}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="cc-fine">Your settings did not change.</p>
                  )}
                  <p className="cc-fine">{moved.length ? `What changed in the plan: ${andList(moved)}.` : "The plan's parts read the same."}</p>
                </div>
              )}
              {versions === null ? (
                <p className="cc-fine">Loading the versions…</p>
              ) : (
                <VersionList
                  head={false}
                  versions={vers}
                  current={current}
                  previous={(v) => vers.filter((x) => x.n < v.n).at(-1) ?? null}
                  ask={() => null}
                  canUse={(v) => v.id !== current && APPROVED.includes(g.state)}
                  acts={{ busy, compare: (from, to) => setCmp({ from, to }), use: async (v) => setReview(v) }}
                  where="plan"
                />
              )}
            </div>
          ) : (
            <div className="dw-body">
              {B[p.id]}
              {changed(p.id) && (
                <details className="cc-before">
                  <summary>
                    <Icon name="chev" />
                    How it read in version {prev?.version}
                  </summary>
                  <div className="cc-before-in">{oldB[p.id]}</div>
                </details>
              )}
            </div>
          )
        }
      />
      {cmp && <Compare versions={vers} from={cmp.from} to={cmp.to} load={load} onPick={(from, to) => setCmp({ from, to })} onClose={() => setCmp(null)} />}
      {review && latest && (
        <Compare
          versions={vers}
          from={verOf(latest)}
          to={review}
          load={load}
          onClose={() => setReview(null)}
          eyebrow="Use this version"
          title={`Bring back version ${review.n}?`}
          sub={`${review.note ?? ""} · ${review.by}, ${whenOf(review.at)}`}
          lede={metaHolds ? "Where the ads show and who sees them stay as they are on Meta." : undefined}
          foot={
            <>
              <span className="note">It becomes version {next}, in force from now. Nothing is deleted.</span>
              <button type="button" className="btn sm ghost" onClick={() => setReview(null)}>
                Cancel
              </button>
              <button type="button" className="btn sm" disabled={busy} onClick={() => void bringBack(review)}>
                Approve
              </button>
            </>
          }
        />
      )}
      {toast}
    </div>
  );
}
